import React from "react";
import { Row, Col } from "react-bootstrap";
import { Icon } from "@iconify/react";
import styles from './AccessosWiki.module.scss';

/**
 * 🛍️ Wiki - Seção Loja
 * 
 * Documentação completa sobre a Loja da plataforma.
 * Explica como navegar, encontrar produtos e se tornar afiliado.
 * Voltada para produtores e afiliados (não técnica).
 */
const LojaWiki = () => {
  return (
    <div className={styles.wikiAccessos}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBadge}>
          <Icon icon="mdi:store" />
          <span>Loja de Produtos</span>
        </div>
        <p className={styles.heroText}>
          Descubra produtos digitais incríveis e comece a ganhar dinheiro promovendo-os. 
          A loja é o lugar onde você encontra produtos para promover como afiliado.
        </p>
      </section>

      {/* O que é a Loja */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>O que é a Loja?</span>
          <p className={styles.sectionSubtitle}>
            Catálogo de produtos disponíveis para afiliação
          </p>
        </div>

        <div className={styles.infoCard}>
          <Icon icon="mdi:information" className={styles.infoIcon} />
          <div>
            <p>
              A <strong>Loja</strong> é o catálogo de todos os produtos digitais disponíveis 
              na plataforma que você pode <strong>promover como afiliado</strong>. Aqui você 
              encontra produtos de diversos produtores, organizados por categorias.
            </p>
            <p style={{ marginTop: '12px', marginBottom: 0 }}>
              Ao encontrar um produto interessante, você pode <strong>solicitar se tornar afiliado</strong> 
              e começar a ganhar comissões promovendo-o!
            </p>
          </div>
        </div>
      </section>

      {/* Como Acessar */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Acessar a Loja?</span>
          <p className={styles.sectionSubtitle}>
            Encontre no menu lateral
          </p>
        </div>

        <Row className="g-4">
          <Col lg={12}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>1</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:menu" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Menu Lateral
                </span>
                <p>
                  No <strong>menu lateral</strong>, acesse <strong>Loja → Loja</strong>. 
                  A loja está disponível para todos os usuários.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Funcionalidades da Loja */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Funcionalidades da Loja</span>
          <p className={styles.sectionSubtitle}>
            O que você pode fazer na loja
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconSuccess}`}>
                <Icon icon="mdi:magnify" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeSuccess}`}>Busca</div>
                <span className={styles.cardTitle}>Buscar Produtos</span>
                <p>
                  Use a <strong>barra de busca</strong> para encontrar produtos específicos 
                  por nome, descrição ou palavras-chave. A busca é instantânea!
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconAffiliate}`}>
                <Icon icon="mdi:filter" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeAffiliate}`}>Filtros</div>
                <span className={styles.cardTitle}>Filtrar por Categoria</span>
                <p>
                  Filtre produtos por <strong>categoria</strong> para encontrar exatamente 
                  o que você procura. Exemplos: Cursos, E-books, Software, etc.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconPlatform}`}>
                <Icon icon="mdi:eye" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgePlatform}`}>Visualizar</div>
                <span className={styles.cardTitle}>Ver Detalhes do Produto</span>
                <p>
                  Clique em qualquer produto para ver <strong>informações completas</strong>: 
                  descrição, preços, planos disponíveis e dados do produtor.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconWarning}`}>
                <Icon icon="mdi:hand-coin" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeWarning}`}>Promover</div>
                <span className={styles.cardTitle}>Solicitar Afiliação</span>
                <p>
                  Na página do produto, clique em <strong>"Promover este Produto"</strong> 
                  para solicitar se tornar afiliado e começar a ganhar comissões!
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Como Solicitar Afiliação */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Solicitar Afiliação de um Produto?</span>
          <p className={styles.sectionSubtitle}>
            Passo a passo para se tornar afiliado
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>1</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:store-search" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Navegue pela Loja
                </span>
                <p>
                  Use a busca ou filtros para encontrar um produto que você gostaria de promover. 
                  Clique no produto para ver os detalhes.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>2</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:information-outline" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Leia os Detalhes
                </span>
                <p>
                  Leia a <strong>descrição completa</strong>, veja os <strong>preços e planos</strong>, 
                  e entenda o que o produto oferece antes de solicitar afiliação.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>3</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:hand-coin" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Clique em "Promover este Produto"
                </span>
                <p>
                  Na página do produto, encontre o botão <strong>"Promover este Produto"</strong> 
                  e clique nele. Uma solicitação será enviada ao produtor.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>4</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:clock-outline" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Aguarde Aprovação
                </span>
                <p>
                  O produtor receberá uma notificação e irá <strong>analisar sua solicitação</strong>. 
                  Você receberá uma notificação quando for aprovado ou rejeitado.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Informações dos Produtos */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Informações dos Produtos</span>
          <p className={styles.sectionSubtitle}>
            O que você vê na página de cada produto
          </p>
        </div>

        <div className={styles.infoCard}>
          <Icon icon="mdi:package-variant" className={styles.infoIcon} />
          <div>
            <p>
              Na página de detalhes de um produto, você encontra:
            </p>
            <ul style={{ marginTop: '12px', marginBottom: 0, paddingLeft: '20px' }}>
              <li><strong>Nome e Descrição:</strong> Informações completas sobre o produto</li>
              <li><strong>Imagem:</strong> Visual do produto</li>
              <li><strong>Preços e Planos:</strong> Diferentes opções de compra disponíveis</li>
              <li><strong>Dados do Produtor:</strong> Nome e informações de contato</li>
              <li><strong>Status:</strong> Se o produto está ativo e disponível</li>
              <li><strong>Botão "Promover":</strong> Para solicitar afiliação</li>
            </ul>
            <p style={{ marginTop: '12px', marginBottom: 0 }}>
              <strong>Importante:</strong> Se você for o próprio produtor do produto, o botão 
              "Promover este Produto" estará desabilitado, pois você não pode ser afiliado 
              do seu próprio produto.
            </p>
          </div>
        </div>
      </section>

      {/* Dicas e Boas Práticas */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Dicas e Boas Práticas</span>
          <p className={styles.sectionSubtitle}>
            Como escolher os melhores produtos para promover
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:target" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Escolha Produtos Relevantes
                </span>
                <p>
                  Promova apenas produtos que <strong>fazem sentido para seu público</strong>. 
                  Produtos relevantes têm maior taxa de conversão!
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:check-circle" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Conheça o Produto
                </span>
                <p>
                  Antes de promover, <strong>leia a descrição completa</strong> e entenda o que 
                  o produto oferece. Você precisa conhecer para recomendar!
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:account-group" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Verifique o Produtor
                </span>
                <p>
                  Veja as <strong>informações do produtor</strong> e verifique se é confiável. 
                  Um bom produtor responde rápido e oferece suporte!
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:chart-line" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Diversifique seu Portfólio
                </span>
                <p>
                  Não coloque todos os ovos em uma cesta! <strong>Promova vários produtos</strong> 
                  de diferentes categorias para maximizar suas oportunidades.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* FAQ */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Perguntas Frequentes</span>
          <p className={styles.sectionSubtitle}>
            Tire suas dúvidas sobre a Loja
          </p>
        </div>

        <Row className="g-4">
          <Col lg={12}>
            <div className={styles.infoCard}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <p style={{ marginBottom: '8px' }}>
                  <strong>Posso ver meus próprios produtos na loja?</strong>
                </p>
                <p style={{ marginBottom: '16px' }}>
                  Não! A loja mostra apenas produtos de <strong>outros produtores</strong>. 
                  Seus próprios produtos não aparecem na loja para você, pois você não pode 
                  ser afiliado do seu próprio produto.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={12}>
            <div className={styles.infoCard}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <p style={{ marginBottom: '8px' }}>
                  <strong>Quantos produtos posso solicitar afiliação?</strong>
                </p>
                <p style={{ marginBottom: '16px' }}>
                  Não há limite! Você pode solicitar afiliação de <strong>quantos produtos 
                  quiser</strong>. Quanto mais produtos relevantes você promover, mais 
                  oportunidades de ganhar comissões você terá.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={12}>
            <div className={styles.infoCard}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <p style={{ marginBottom: '8px' }}>
                  <strong>O que acontece se minha solicitação for rejeitada?</strong>
                </p>
                <p style={{ marginBottom: '16px' }}>
                  Se sua solicitação for rejeitada, você receberá uma notificação. 
                  O produto continuará aparecendo na loja, mas você não poderá promovê-lo. 
                  Você pode tentar solicitar novamente no futuro.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>
    </div>
  );
};

export default LojaWiki;

